import type { PublicBanner } from "../../shared/banners";
import type { CouponQuote } from "../../shared/coupons";
import type { CollectionView } from "../../shared/milestones";
import type { CatalogFilters } from "../../shared/filters";
import { formatArs, formatUsd } from "../../shared/money";
import { serializeFilters } from "../../shared/filters";
import { actAsHeaders } from "./actAs";
import { resizeImage } from "./resizeImage";
import type { ApiResponse, Catalog, CatalogFacets, Localized, Product, ProductDetail, OrderInput, OrderReceipt, OrderSummary, PaymentConfig, ProductPage, SiteStatus } from "../../shared/types";
import { GAME_PARAM } from "../../shared/games";

/** Longest side an avatar is sent at (it's shown small; the server crops it square). */
const AVATAR_SIDE = 800;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers: { Accept: "application/json", ...actAsHeaders(path, init?.method), ...init?.headers } });
  } catch (err) {
    throw new ApiError(0, "network_error", err instanceof Error ? err.message : "Network error");
  }
  let body: ApiResponse<T> | null = null;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    // Non-JSON response (e.g. proxy error page); handled below.
  }
  if (!res.ok || !body || !body.success) {
    const error = body && !body.success ? body.error : null;
    throw new ApiError(res.status, error?.code ?? "http_error", error?.message ?? `HTTP ${res.status}`, error?.details);
  }
  return body.data;
}

export interface ShowcaseCard {
  slug: string;
  name: Localized;
  imageKey: string;
  /** USD cents. */
  price: number;
}

export interface Profile {
  name: string;
  email: string;
  avatarUrl: string | null;
  marketingOptIn: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  addressLine: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
}

export type ProfileInput = Omit<Profile, "name" | "email" | "avatarUrl">;

export interface Watch {
  id: number;
  kind: "stock" | "price";
  product_id: number;
  base_price: number | null;
  target_price: number | null;
  created_at: string;
  slug: string;
  name_es: string;
  name_en: string;
  image_key: string;
  price: number;
  stock: number;
}

export const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  credentials: "same-origin",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** Subscribed to news, the welcome offer and this account's personal code (server/lib/newsletterOffer.ts). */
export interface NewsletterStatus {
  subscribed: boolean;
  verified: boolean;
  offer: { enabled: boolean; pct: number; days: number; excludedCategories: string[] };
  coupon: { code: string; pct: number; endsAt: string | null; used: boolean; excluded: string[] } | null;
}

export const api = {
  products: (filters: CatalogFilters, signal?: AbortSignal) =>
    request<ProductPage>(`/api/products?${serializeFilters(filters).toString()}`, { signal }),
  product: (slug: string, signal?: AbortSignal) =>
    request<ProductDetail>(`/api/products/${encodeURIComponent(slug)}`, { signal }),
  catalog: (signal?: AbortSignal) => request<Catalog>("/api/catalog", { signal }),
  /** The search box's suggestions for what's typed (2+ characters). */
  suggest: (q: string, game: string, signal?: AbortSignal) =>
    request<{ products: Product[] }>(`/api/search?q=${encodeURIComponent(q)}&${GAME_PARAM}=${encodeURIComponent(game)}`, { signal }),
  /** Sets and rarities for the filters, scoped to the chosen games/categories/stock. */
  /** The three most expensive cards in stock (home banner). */
  showcase: (game: string, signal?: AbortSignal) => request<ShowcaseCard[]>(`/api/showcase?${GAME_PARAM}=${encodeURIComponent(game)}`, { signal }),
  /** The home page carousel (live banners, products resolved). */
  banners: (signal?: AbortSignal) => request<PublicBanner[]>("/api/banners", { signal }),
  facets: (filters: Pick<CatalogFilters, "games" | "categories" | "inStock" | "sets">, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (filters.games.length) params.set("game", filters.games.join(","));
    if (filters.categories.length) params.set("category", filters.categories.join(","));
    if (!filters.inStock) params.set("stock", "0");
    for (const s of filters.sets) params.append("set", s);
    return request<CatalogFacets>(`/api/facets?${params}`, { signal });
  },
  settings: (signal?: AbortSignal) => request<Record<string, Localized>>("/api/settings", { signal }),
  site: (signal?: AbortSignal) => request<SiteStatus>("/api/site", { signal }),
  /** A coupon for this cart (422 coupon_invalid with details.coupon = i18n key when it doesn't apply). */
  /** `email`: buying without an account, the order's email (it counts the coupon's uses). */
  checkCoupon: (code: string, items: { variantId: number; quantity: number }[], email?: string) =>
    request<CouponQuote>("/api/coupons/check", jsonInit("POST", { code, items, ...(email ? { email } : {}) })),
  createOrder: (input: OrderInput) =>
    request<OrderReceipt>("/api/orders", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
  myOrders: (signal?: AbortSignal) => request<OrderSummary[]>("/api/orders", { signal, credentials: "same-origin" }),
  /** key: the order's access key from payment returns and emails (works without signing in). */
  order: (orderNumber: string, signal?: AbortSignal, key?: string | null) =>
    request<OrderSummary>(`/api/orders/${encodeURIComponent(orderNumber)}${key ? `?k=${encodeURIComponent(key)}` : ""}`, { signal, credentials: "same-origin" }),
  profile: (signal?: AbortSignal) => request<Profile>("/api/me/profile", { signal, credentials: "same-origin" }),
  saveProfile: (input: ProfileInput) => request<Profile>("/api/me/profile", jsonInit("PUT", input)),
  newsletter: (signal?: AbortSignal) => request<NewsletterStatus>("/api/me/newsletter", { signal, credentials: "same-origin" }),
  subscribeNewsletter: () => request<NewsletterStatus>("/api/me/newsletter", jsonInit("POST", {})),
  uploadAvatar: async (image: Blob) => {
    const form = new FormData();
    form.append("image", await resizeImage(image, { maxSide: AVATAR_SIDE }), "avatar");
    return request<{ avatarUrl: string }>("/api/me/avatar", { method: "POST", body: form, credentials: "same-origin" });
  },
  removeAvatar: () => request<{ avatarUrl: null }>("/api/me/avatar", { method: "DELETE", credentials: "same-origin" }),
  /** Card avatar: level, milestones and the starter's cards (also records new milestones). */
  collection: (signal?: AbortSignal) => request<CollectionView>("/api/me/collection", { signal, credentials: "same-origin" }),
  chooseStarter: (starter: string) => request<CollectionView>("/api/me/collection/starter", jsonInit("PUT", { starter })),
  setAvatarChoice: (choice: { mode: "card"; level: number | null } | { mode: "photo" }) => request<CollectionView>("/api/me/collection/avatar", jsonInit("PUT", choice)),
  collectionSeen: (level: number) => request<{ seen: true }>("/api/me/collection/seen", jsonInit("POST", { level })),
  unsubscribe: (u: string, t: string) => request<{ unsubscribed: true }>("/api/email/unsubscribe", jsonInit("POST", { u, t })),
  cart: () => request<{ lines: unknown[]; updatedAt: string | null }>("/api/me/cart", { credentials: "same-origin" }),
  saveCart: (lines: unknown[]) => request<{ saved: true }>("/api/me/cart", jsonInit("PUT", { lines })),
  watches: (signal?: AbortSignal) => request<Watch[]>("/api/me/watches", { signal, credentials: "same-origin" }),
  createWatch: (productId: number, kind: "stock" | "price", targetPrice: number | null = null) =>
    request<{ id: number }>("/api/me/watches", jsonInit("POST", { productId, kind, targetPrice })),
  deleteWatch: (id: number) => request<{ deleted: true }>(`/api/me/watches/${id}`, { method: "DELETE", credentials: "same-origin" }),
  /** "Botón de arrepentimiento": no account needed. */
  requestWithdrawal: (input: { orderNumber: string; name: string; email: string; phone: string; reason: string; locale: "es" | "en" }) =>
    request<{ code: string; orderFound: boolean }>("/api/withdrawals", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
  paymentConfig: (signal?: AbortSignal) => request<PaymentConfig>("/api/payments/config", { signal }),
  capturePayPal: (orderNumber: string, key?: string | null) =>
    request<OrderSummary>("/api/payments/paypal/capture", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderNumber, k: key ?? undefined }),
    }),
};

/** Formats a provider charge (minor units) in its own currency. */
export function formatCharge(amountMinor: number, currency: "ARS" | "USD", locale: "es" | "en"): string {
  return currency === "USD" ? formatUsd(amountMinor, locale) : formatArs(Math.round(amountMinor / 100), locale);
}

/** Where product images are served from: the server injects it (R2 domain in production), "/img" otherwise. */
function imageBase(): string {
  if (typeof document === "undefined") return "/img";
  return document.querySelector<HTMLMetaElement>('meta[name="pokefans:image-base"]')?.content || "/img";
}

export function imageUrl(key: string): string {
  return `${imageBase()}/${key.split("/").map(encodeURIComponent).join("/")}`;
}
